/**
 * Reference for the Geolocation API:
 * https://developer.mozilla.org/en-US/docs/Web/API/Geolocation
 */

var DEFAULT_ACCURACY = 40
var ACCURACY_LIMIT = 200
var AUDIO_ACCURACY = 100
var SIGNIFICANTLY_NEWER_THRESHOLD = 30 * 60 * 1000 // half minute

/**
 * Responsible for collecting gps data through navigator.geolocation (if supported by the device).
 * The user must opt-in to their device's gps settings to allow location tracking to take place.
 *
 * @param {Object} opts Options.
 * @param {Object} opts.location Location object that contains loc_id, min_lat, min_lng, max_lat, max_lng properties
 * @param {array}  opts.stops An array of stops. Each stop must have real_lat, real_lng, and radius properties
 */
function GpsCollector(opts) {
    opts = opts || {}

    if (!opts.location) {
        console.error('GpsCollector required "location" as a parameter.')
    }

    this._watch = null
    this._bestPosition = null
    this._currentGpsObject = null
    this._location = opts.location
    this._stops = opts.stops || []
    this._listeners = []
    this._errorListeners = []
}

/**
 * Does navigator.geolocation exist?
 *
 * @return {Boolean} Returns if navigator.geolocation exists.
 */
GpsCollector.isSupported = function() {
    return typeof navigator.geolocation !== 'undefined'
}

GpsCollector.prototype = {
    /**
     * Is a watch currently active?
     *
     * @return {Boolean} Returns if watch is actice.
     */
    isWatching: function() {
        return this._watch !== null
    },

    /**
     * Starts the navigator.geolocation watch
     */
    startWatch: function() {
        // START GPS WATCH
        if (GpsCollector.isSupported() && !this.isWatching()) {
            this._watch = navigator.geolocation.watchPosition(
                this._onPositionChange.bind(this),
                this._onPositionError.bind(this),
                {
                    maximumAge: 5000,
                    enableHighAccuracy: true,
                }
            )
        }
    },

    /**
     * Ends the navigator.geolocation watch
     */
    endWatch: function() {
        // CLEAR GPS WATCH
        if (this._watch !== null) {
            navigator.geolocation.clearWatch(this._watch)
            this._watch = null
        }
    },

    /**
     * Manually query your position.
     *
     * @param  {Function} callback      Callback with gps object on success.
     * @param  {Function} errorCallback Callback with position error.
     * @param  {Object} opts            Options to pass into navigator.geolocation.getCurrentPosition
     */
    getPosition: function(callback, errorCallback, opts) {
        if (GpsCollector.isSupported()) {
            var wasWatching = this.isWatching()

            // the watch must end before position can be queried
            this.endWatch()

            navigator.geolocation.getCurrentPosition(
                this._onGetPosition.bind(this, wasWatching, callback),
                this._onGetPositionError.bind(this, wasWatching, errorCallback),
                opts
            )
        }
    },

    /**
     * Gets the most recently created GPS object, even if it was created outside of location bounds.
     *
     * @return {Object} GPS object (null if none are current)
     */
    getCurrentGpsObject: function() {
        return this._currentGpsObject
    },

    /**
     * Adds a listener for watch.
     *
     * @param {Function} callback Listener.
     */
    addListener: function(callback) {
        this._listeners.push(callback)
    },

    /**
     * Removes a listener from watch.
     *
     * @param {Function} callback Listener.
     */
    removeListener: function(callback) {
        var i = this._listeners.indexOf(callback)
        if (i > -1) {
            this._listeners.splice(i, 1)
        }
    },

    /**
     * Adds an error listener for watch.
     *
     * @param {Function} callback Listener.
     */
    addErrorListener: function(callback) {
        this._errorListeners.push(callback)
    },

    /**
     * Removes an error listener for watch.
     *
     * @param {Function} callback Listener.
     */
    removeErrorListener: function(callback) {
        var i = this._errorListeners.indexOf(callback)
        if (i > -1) {
            this._errorListeners.splice(i, 1)
        }
    },

    /**
     * Returns the distance between two gps objects. Gps objects are returned by watch listeners and manual position queries.
     * If either of the objects are null, the distance returned is 0.
     *
     * @param  {Object}  gpsObject1 GPS object 1
     * @param  {Object}  gpsObject2 GPS object 2
     * @return {number}   Distance between those two points.
     */
    distance: function(gpsObject1, gpsObject2) {
        if (!gpsObject1 || !gpsObject2) {
            return 0
        }
        return this._distance(gpsObject1.lat, gpsObject1.lng, gpsObject2.lat, gpsObject2.lng)
    },

    /**
     * Is gps object accurate?
     *
     * @param  {Object}   gpsObject GPS object
     * @return {Boolean}  True if gps object is within accuracy threshold.
     */
    isGpsAccurate: function(gpsObject) {
        return gpsObject && this._isAccurate(gpsObject.accuracy)
    },

    /**
     * Is gps within audio accuracy? This is the same behavior that GpsManager/mapController.
     *
     * @param  {Object}   gpsObject GPS object
     * @return {Boolean}  True if gps object is within audio accuracy threshold.
     */
    isGpsWithinAudioAccuracy: function(gpsObject) {
        return gpsObject && gpsObject.accuracy <= AUDIO_ACCURACY
    },

    /**
     * Is gps object within location bounds?
     *
     * @param  {Object}   gpsObject GPS object
     * @return {Boolean}  True if gps object is within location bounds.
     */
    isGpsInBounds: function(gpsObject) {
        return gpsObject && this._isInBounds(gpsObject.lat, gpsObject.lng)
    },

    /**
     * Creates the public gps object from the raw navigator.geolocation returned positions.
     *
     * @param  {Object}  position Position from navigator.geolocation routines.
     * @return {Object}  GPS object - exposed to public.
     */
    _makeGpsObject: function(position) {
        var gpsObject = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            loc_id: this._location.loc_id,
        }

        var stop = this._getStopAtPosition(position)
        if (stop) {
            gpsObject['stopid'] = stop.stopid
        }

        // UPDATE CURRENT GPS OBJECT
        this._currentGpsObject = gpsObject

        return gpsObject
    },

    /**
     * Private callback for navigator.geolocation.getCurrentPosition.
     *
     * @param  {Boolean}   wasWatching  Should watch be restarted?
     * @param  {Function}  callback     Callback. Returns a GPS Object.
     * @param  {Object}    position     Position object returned by navigator.geolocation.getCurrentPosition
     */
    _onGetPosition: function(wasWatching, callback, position) {
        if (wasWatching) {
            this.startWatch()
        }

        var gpsObject = this._makeGpsObject(position)

        if (callback) {
            callback(gpsObject)
        }
    },

    /**
     * Private error callback for navigator.geolocation.getCurrentPosition on error.
     *
     * @param  {Boolean}  wasWatching    Should watch be restarted?
     * @param  {Function} callback       Callback. Returns PositionError.
     * @param  {Object}   positionError  PositionError
     */
    _onGetPositionError: function(wasWatching, callback, positionError) {
        if (wasWatching) {
            this.startWatch()
        }

        if (callback) {
            callback(positionError)
        }
    },

    /**
     * Private callback for navigator.geolocation.watchPosition. Performs several checks on the position to determine
     * if it's better position, accurate, and within bounds. This mimics the functionality that was previously in GpsManager.
     * Invokes listeners with new gps object if valid.
     *
     * @param  {Object} position  Position object from navigator.geolocation.watchPosition
     */
    _onPositionChange: function(position) {
        if (!this._isPositionBetter(position)) {
            return
        }
        this._bestPosition = position

        var coords = position.coords

        if (!this._isAccurate(coords.accuracy)) {
            return
        }

        if (!this._isInBounds(coords.latitude, coords.longitude)) {
            return
        }

        var gpsObject = this._makeGpsObject(position)

        var listeners = this._listeners
        for (var i = listeners.length - 1; i >= 0; --i) {
            listeners[i](gpsObject)
        }
    },

    /**
     * Private error callback for navigator.geolocation.watchPosition. Invokes error listeners.
     *
     * @param  {PositionError} positionError  PositionError from navigator.geolocation.watchPosition's error callback.
     */
    _onPositionError: function(positionError) {
        var listeners = this._errorListeners
        for (var i = listeners.length - 1; i >= 0; --i) {
            listeners[i](positionError)
        }
    },

    /**
     * Private method mimics prior behavior from GeoManager to determine if Position is better.
     *
     * @param  {Object}  position  Position object returned by navigator.geolocation.watchPosition
     * @return {Boolean}           Returns if position is significantly newer or more accurate.
     */
    _isPositionBetter: function(position) {
        var bestPosition = this._bestPosition

        if (!bestPosition) {
            return true
        }

        var deltaTime = position.timestamp - bestPosition.timestamp
        var isNewer = deltaTime > 0

        // is significantly newer
        if (deltaTime > SIGNIFICANTLY_NEWER_THRESHOLD) {
            return true
        }
        // is significantly older
        else if (deltaTime < -SIGNIFICANTLY_NEWER_THRESHOLD) {
            return false
        }

        var deltaAccuracy = position.coords.accuracy - bestPosition.coords.accuracy

        // is more accurate
        if (deltaAccuracy < 0) {
            return true
        }
        // is newer point with at least decent accuracy
        else if (isNewer && deltaAccuracy < 100) {
            return true
        }

        return false
    },

    /**
     * Private method to check if accuracy is within accuracy threshold.
     *
     * @param  {number}  accuracy  Accuracy.
     * @return {Boolean}          Returns if accuracy is less than or equal to limit.
     */
    _isAccurate: function(accuracy) {
        return accuracy <= ACCURACY_LIMIT
    },

    /**
     * Private method to check if latitude/longitude pair is within location bounds.
     *
     * @param  {number}  lat  Latitude.
     * @param  {number}  lng  Longitude.
     * @return {Boolean}     True if lat/lng is within location bounds.
     */
    _isInBounds: function(lat, lng) {
        var location = this._location

        return lat < location.min_lat && lng > location.min_lng && lat > location.max_lat && lng < location.max_lng
    },

    /**
     * Private method to get closest stop at position, or null if outside stop.
     *
     * @param  {Object} position  Position object from navigator.geolocation
     * @return {Object|null}      Closest stop at position or null.
     */
    _getStopAtPosition: function(position) {
        var lat = position.coords.latitude
        var lng = position.coords.longitude

        // GET CLOSEST STOP WITHIN RADIUS
        var closestStop = null
        var closestDist = Infinity

        var stops = this._stops
        for (var s in stops) {
            var stop = stops[s]

            var dist = this._distance(lat, lng, stop.real_lat, stop.real_lng)

            if (dist <= stop.radius && dist < closestDist) {
                closestStop = stop
                closestDist = dist
            }
        }
        return closestStop
    },

    /**
     * Convert degrees to radians.
     *
     * @param  {number} deg Degrees.
     * @return {number}     Radians.
     */
    _degToRad: function(deg) {
        return (deg * Math.PI) / 180
    },

    /**
     * Private method to compute distance in meters.
     *
     * @param  {number} lat1 Latitude 1
     * @param  {number} lon1 Longitude 1
     * @param  {number} lat2 Latitude 2
     * @param  {number} lon2 Longitude 2
     * @return {number}      Distance between lat1/lng1 and lat2/lng2 in meters.
     */
    _distance: function(lat1, lon1, lat2, lon2) {
        var R = 6371 // earth's mean radius in km
        var dLat = this._degToRad(lat2 - lat1)
        var dLon = this._degToRad(lon2 - lon1)

        lat1 = this._degToRad(lat1)
        lat2 = this._degToRad(lat2)

        var a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        var d = R * c
        return 1000 * d
    },
}
export default GpsCollector
